/**
 * Vitals catalog.
 *
 * Each vital sign is its own FHIR R4 vital-signs `Observation`; one panel Observation (LOINC 85353-1)
 * groups a set through `hasMember`. Blood pressure is one Observation (85354-9) with systolic and
 * diastolic `component`s and no top-level value. Codes and UCUM units follow #6014 "Vitals to collect
 * and how they are stored". The catalog is fixed: accounts hide default vitals and switch optional ones
 * on, but never define their own.
 *
 * Entry fields: key, label, kind ('quantity' | 'bloodPressure'), codings (primary first, extra codings
 * after), componentCodings (blood pressure only), unit ({ code, display }, system always UCUM), entryUnit,
 * measurementLabel (the unit as people read it, e.g. 'Feet & inches'), profile, position (blood pressure and heart rate only), isDefault, calculatedFrom (calculated vitals
 * only) and plausible (a warning range that never blocks saving).
 */

export const LOINC_SYSTEM = 'http://loinc.org';
export const SNOMED_SYSTEM = 'http://snomed.info/sct';
export const UCUM_SYSTEM = 'http://unitsofmeasure.org';
export const OBSERVATION_CATEGORY_SYSTEM = 'http://terminology.hl7.org/CodeSystem/observation-category';
export const VITALS_CODE_SYSTEM = 'http://fhir.bestnotes.com/CodeSystem/vitals';
export const VITALS_INTERPRETATION_SYSTEM = 'http://fhir.bestnotes.com/CodeSystem/vitals-interpretation';
export const BODY_POSITION_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/observation-bodyPosition';

const PROFILE_BASE = 'http://hl7.org/fhir/StructureDefinition/';
export const VITAL_SIGNS_PROFILE = PROFILE_BASE + 'vitalsigns';
export const PANEL_PROFILE = PROFILE_BASE + 'vitalspanel';

export const PANEL_CODING = { system: LOINC_SYSTEM, code: '85353-1', display: 'Vital signs, weight, height, head circumference, oxygen saturation and BMI panel' };

export const VITAL_SIGNS_CATEGORY = { coding: [{ system: OBSERVATION_CATEGORY_SYSTEM, code: 'vital-signs', display: 'Vital Signs' }] };

export const BODY_POSITIONS = {
    sitting: { system: SNOMED_SYSTEM, code: '33586001', display: 'Sitting position' },
    standing: { system: SNOMED_SYSTEM, code: '10904000', display: 'Orthostatic body position' },
    supine: { system: SNOMED_SYSTEM, code: '40199007', display: 'Supine body position' },
};

const loinc = (code, display) => ({ system: LOINC_SYSTEM, code, display });
const bestNotes = (code, display) => ({ system: VITALS_CODE_SYSTEM, code, display });

const SYSTOLIC = loinc('8480-6', 'Systolic blood pressure');
const DIASTOLIC = loinc('8462-4', 'Diastolic blood pressure');
// The primary coding that identifies each blood-pressure component, whatever the position.
export const BP_COMPONENT_CODINGS = { systolic: SYSTOLIC, diastolic: DIASTOLIC };
const BLOOD_PRESSURE = loinc('85354-9', 'Blood pressure panel with all children optional');
const HEART_RATE = loinc('8867-4', 'Heart rate');

const UNITS = {
    inches: { code: '[in_i]', display: 'in' },
    pounds: { code: '[lb_av]', display: 'lb' },
    bmi: { code: 'kg/m2', display: 'kg/m2' },
    fahrenheit: { code: '[degF]', display: '°F' },
    percent: { code: '%', display: '%' },
    perMinute: { code: '/min', display: '/min' },
    mmHg: { code: 'mm[Hg]', display: 'mmHg' },
    score: { code: '{score}', display: 'score' },
};

const WEIGHT_PLAUSIBLE = { min: 1, max: 1000 };
const PERCENT_OF_WEIGHT_PLAUSIBLE = { min: 20, max: 400 };
const BP_PLAUSIBLE = { systolic: { min: 50, max: 300 }, diastolic: { min: 20, max: 200 } };
const HEART_RATE_PLAUSIBLE = { min: 25, max: 250 };

export const VITALS = [
    {
        key: 'height',
        label: 'Height',
        kind: 'quantity',
        codings: [loinc('8302-2', 'Body height')],
        unit: UNITS.inches,
        entryUnit: 'ft/in',
        measurementLabel: 'Feet & inches',
        profile: PROFILE_BASE + 'bodyheight',
        isDefault: true,
        plausible: { min: 12, max: 108 },
    },
    {
        key: 'weight',
        label: 'Weight',
        kind: 'quantity',
        codings: [loinc('29463-7', 'Body weight')],
        unit: UNITS.pounds,
        entryUnit: 'lb',
        measurementLabel: 'lbs',
        profile: PROFILE_BASE + 'bodyweight',
        isDefault: true,
        plausible: WEIGHT_PLAUSIBLE,
    },
    {
        key: 'bmi',
        label: 'BMI',
        kind: 'quantity',
        codings: [loinc('39156-5', 'Body mass index (BMI) [Ratio]')],
        unit: UNITS.bmi,
        entryUnit: 'kg/m2',
        measurementLabel: 'kg/m²',
        profile: PROFILE_BASE + 'bmi',
        isDefault: true,
        calculatedFrom: ['height', 'weight'],
        plausible: { min: 8, max: 120 },
    },
    {
        key: 'temperature',
        label: 'Temperature',
        kind: 'quantity',
        codings: [loinc('8310-5', 'Body temperature')],
        unit: UNITS.fahrenheit,
        entryUnit: '°F',
        measurementLabel: '°F',
        profile: PROFILE_BASE + 'bodytemp',
        isDefault: true,
        plausible: { min: 85, max: 113 },
    },
    {
        key: 'oxygenSaturation',
        label: 'Oxygen saturation',
        kind: 'quantity',
        codings: [loinc('2708-6', 'Oxygen saturation in Arterial blood'), loinc('59408-5', 'Oxygen saturation in Arterial blood by Pulse oximetry')],
        unit: UNITS.percent,
        entryUnit: '%',
        measurementLabel: '%',
        profile: PROFILE_BASE + 'oxygensat',
        isDefault: true,
        plausible: { min: 50, max: 100 },
    },
    {
        key: 'respiratoryRate',
        label: 'Respiratory rate',
        kind: 'quantity',
        codings: [loinc('9279-1', 'Respiratory rate')],
        unit: UNITS.perMinute,
        entryUnit: '/min',
        measurementLabel: 'breaths/min',
        profile: PROFILE_BASE + 'resprate',
        isDefault: true,
        plausible: { min: 4, max: 70 },
    },
    {
        key: 'bpSitting',
        label: 'Sitting blood pressure',
        kind: 'bloodPressure',
        codings: [BLOOD_PRESSURE],
        componentCodings: { systolic: [SYSTOLIC], diastolic: [DIASTOLIC] },
        unit: UNITS.mmHg,
        entryUnit: 'mmHg',
        measurementLabel: 'mmHg',
        profile: PROFILE_BASE + 'bp',
        position: 'sitting',
        isDefault: true,
        plausible: BP_PLAUSIBLE,
    },
    {
        key: 'heartRateSitting',
        label: 'Sitting heart rate',
        kind: 'quantity',
        codings: [HEART_RATE],
        unit: UNITS.perMinute,
        entryUnit: '/min',
        measurementLabel: 'beats/min',
        profile: PROFILE_BASE + 'heartrate',
        position: 'sitting',
        isDefault: true,
        plausible: HEART_RATE_PLAUSIBLE,
    },
    {
        key: 'bpStanding',
        label: 'Standing blood pressure',
        kind: 'bloodPressure',
        codings: [BLOOD_PRESSURE],
        componentCodings: {
            systolic: [SYSTOLIC, loinc('8460-8', 'Systolic blood pressure--standing')],
            diastolic: [DIASTOLIC, loinc('8454-1', 'Diastolic blood pressure--standing')],
        },
        unit: UNITS.mmHg,
        entryUnit: 'mmHg',
        measurementLabel: 'mmHg',
        profile: PROFILE_BASE + 'bp',
        position: 'standing',
        isDefault: false,
        plausible: BP_PLAUSIBLE,
    },
    {
        key: 'bpLying',
        label: 'Lying blood pressure',
        kind: 'bloodPressure',
        codings: [BLOOD_PRESSURE],
        componentCodings: {
            systolic: [SYSTOLIC, loinc('8461-6', 'Systolic blood pressure--supine')],
            diastolic: [DIASTOLIC, loinc('8455-8', 'Diastolic blood pressure--supine')],
        },
        unit: UNITS.mmHg,
        entryUnit: 'mmHg',
        measurementLabel: 'mmHg',
        profile: PROFILE_BASE + 'bp',
        position: 'supine',
        isDefault: false,
        plausible: BP_PLAUSIBLE,
    },
    {
        key: 'heartRateStanding',
        label: 'Standing heart rate',
        kind: 'quantity',
        codings: [HEART_RATE, loinc('69001-6', 'Heart rate --standing')],
        unit: UNITS.perMinute,
        entryUnit: '/min',
        measurementLabel: 'beats/min',
        profile: PROFILE_BASE + 'heartrate',
        position: 'standing',
        isDefault: false,
        plausible: HEART_RATE_PLAUSIBLE,
    },
    {
        key: 'heartRateLying',
        label: 'Lying heart rate',
        kind: 'quantity',
        codings: [HEART_RATE, loinc('68999-2', 'Heart rate --supine')],
        unit: UNITS.perMinute,
        entryUnit: '/min',
        measurementLabel: 'beats/min',
        profile: PROFILE_BASE + 'heartrate',
        position: 'supine',
        isDefault: false,
        plausible: HEART_RATE_PLAUSIBLE,
    },
    {
        key: 'idealBodyWeight',
        label: 'Ideal body weight',
        kind: 'quantity',
        codings: [loinc('50064-5', 'Ideal body weight')],
        unit: UNITS.pounds,
        entryUnit: 'lb',
        measurementLabel: 'lbs',
        profile: VITAL_SIGNS_PROFILE,
        isDefault: false,
        plausible: WEIGHT_PLAUSIBLE,
    },
    {
        key: 'percentIdealBodyWeight',
        label: '% of ideal body weight',
        kind: 'quantity',
        codings: [loinc('111541-9', 'Body weight deviation/ideal weight by calculation')],
        unit: UNITS.percent,
        entryUnit: '%',
        measurementLabel: '%',
        profile: VITAL_SIGNS_PROFILE,
        isDefault: false,
        calculatedFrom: ['weight', 'idealBodyWeight'],
        plausible: PERCENT_OF_WEIGHT_PLAUSIBLE,
    },
    {
        key: 'desiredBodyWeight',
        label: 'Desired body weight',
        kind: 'quantity',
        codings: [bestNotes('desired-body-weight', 'Desired body weight')],
        unit: UNITS.pounds,
        entryUnit: 'lb',
        measurementLabel: 'lbs',
        profile: VITAL_SIGNS_PROFILE,
        isDefault: false,
        plausible: WEIGHT_PLAUSIBLE,
    },
    {
        key: 'percentDesiredBodyWeight',
        label: '% of desired body weight',
        kind: 'quantity',
        codings: [bestNotes('percent-desired-body-weight', '% of desired body weight')],
        unit: UNITS.percent,
        entryUnit: '%',
        measurementLabel: '%',
        profile: VITAL_SIGNS_PROFILE,
        isDefault: false,
        calculatedFrom: ['weight', 'desiredBodyWeight'],
        plausible: PERCENT_OF_WEIGHT_PLAUSIBLE,
    },
    {
        key: 'painScore',
        label: 'Pain score',
        kind: 'quantity',
        codings: [loinc('72514-3', 'Pain severity - 0-10 verbal numeric rating [Score] - Reported')],
        unit: UNITS.score,
        entryUnit: '0-10',
        measurementLabel: '0–10 scale',
        profile: VITAL_SIGNS_PROFILE,
        isDefault: false,
        plausible: { min: 0, max: 10 },
    },
];

export const VITALS_BY_KEY = Object.fromEntries(VITALS.map((vital) => [vital.key, vital]));

/** The ranges the Vitals widget's history option offers, in days. */
export const VITALS_HISTORY_DAY_OPTIONS = [30, 60, 90];
